import { AfterViewInit, ChangeDetectionStrategy, Component, Injector } from "@angular/core";
import { AgEditorComponent } from "@bfx/ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { FwdRollMutableRowModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollMutableRowModel";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  AMOUNT_VALUE_ACCESSOR,
  ModelValueType,
  NUMBER_SPINNER_INPUTS,
  NUMBER_SPINNER_VIEW_PROVIDERS,
  NumberSpinnerComponent,
  ZERO,
} from "shared/base-components/number-spinner.component";
import { BfxFieldNumberDirective } from "../../../../shared/base-components/bfx-field-number";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";

/**
 * Base editor component for all Fwd Roll blotter editors - overriding classes just need to provide
 * update method for pushing dynamic changes back into the portfolio model
 */
@Component({
  selector: "df-price-spinner",
  templateUrl: "../../../../shared/base-components/html/number-spinner.html",
  providers: [AMOUNT_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FwdRollBaseEditor
  extends NumberSpinnerComponent
  implements AgEditorComponent, AfterViewInit
{
  protected modelData: FwdRollMutableRowModel;
  protected params: ICellEditorParams;

  protected initValue: CubiqTS.BigNumber;

  constructor(
    injector: Injector,
    inputDirective: BfxInputDirective,
    fieldDirective: BfxFieldNumberDirective,
  ) {
    super(injector, inputDirective, fieldDirective);
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    this.modelData = params.data;

    this.modelValueType = ModelValueType.BIG_NUMBER;
    this.groupNonDecimals = false;
    this.mandatory = true;

    this.initConfiguration();
    this.updateConfiguration();

    if (params.value) {
      this.writeValue(params.value);
      this.initValue = params.value as CubiqTS.BigNumber;
    } else {
      this.initValue = ZERO;
    }
  }

  /**
   * It is essential that the focusInput is called otherwise input will not show focused and
   * tabbing through the columns will fail, probably due to a grid focus check
   */
  ngAfterViewInit() {
    super.ngAfterViewInit();
    setTimeout(() => {
      this.focusInput();
    });
  }

  /**
   * Use Fwd Pts as default as all editors currently use this config
   */
  protected initConfiguration() {
    this.min = null;
    this.minPrecision = this.modelData.ptsPrecision;
    this.maxPrecision = this.modelData.ptsPrecision;
    const stepStr = `1e-${this.modelData.ptsPrecision}`;
    this.step = new CubiqTS.BigNumber(stepStr);
    this.locked = this.modelData.isEditorDisabled;
  }

  /**
   * Rescan the model data and update the locked state
   */
  public updateLocked(): void {
    this.locked = this.modelData.isEditorDisabled;
  }

  /**
   * Ensure that the BigNumber value is used as model value
   * @returns
   */
  getValue(): any {
    return this.getModelValue();
  }

  public override onChange() {
    super.onChange();

    if (this.modelData) {
      this.updateModelValue();
    }
  }

  /**
   * Override to update this field and related fields in the portfolio model
   * @param dirtyCols
   */
  protected updateModelValue() {
    // Do nothing
  }

  protected override updateInputClasses() {
    super.updateInputClasses();
    if (this.data.value && this.initValue && !this.data.value.isEqualTo(this.initValue)) {
      this.inputClasses.push("updated-price");
    }
  }
}
